//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  DimensionValidationRules,
  VariationValidationRules,
} from '@campaigns/types/sampling/SamplingProductValidationTypes';
import { validateNumber } from '@campaigns/utils/FormUtils';
import { isDefined } from '@utils/filterPredicates';
import { URL_REGEX_PROTOCOL_ONLY } from '@utils/urlValidation';

export const URL_REGEX_RULE = new RegExp(URL_REGEX_PROTOCOL_ONLY);

/**
 * Regular expression to validate product IDs.
 *
 * This regex ensures that:
 * - The string does not start with a hyphen (-).
 * - The string ends with a word character (alphanumeric), period (.), or hyphen (-).
 * - The string only contains word characters (alphanumeric), periods (.), hyphens (-), or pipe characters (|).
 *
 * Note: This version does not accept spaces anywhere in the string.
 *
 * Examples of valid IDs:
 * - "product-123"
 * - "item.name"
 * - "A1.B2-C3"
 * - "product|name"
 *
 * Examples of invalid IDs:
 * - " product123" (starts with a whitespace)
 * - "-product123" (starts with a hyphen)
 * - "product 123" (contains a whitespace)
 */
export const PRODUCT_ID_REGEX_RULE = /^(?=[^\-\s])(?=.*[\w.|-]$)[\w.|-]+$/;

/**
 * Validates that a product variant image URL has an http(s):// protocol
 */
export const isProductImageUrlValid = (url: string): boolean => {
  return URL_REGEX_RULE.test(url);
};

/**
 * Validates that the product image content type is supported and that it points to a
 * loadable image resource. The function attempts to load it using an Image element and
 * resolves based on the load result.
 * @param {string} url
 * @returns {Promise<boolean>}
 */
export const isProductImageContentTypeValid = async (
  url: string | undefined,
): Promise<boolean> => {
  if (!url) {
    return true;
  }

  // Use an img element instead of fetch to avoid CORS issues — browsers load images
  // cross-origin without requiring CORS headers, so this works even when CDNs reject
  // preflight requests or don't return Access-Control-Allow-Origin headers
  return new Promise<boolean>((resolve) => {
    const img = new Image();
    const TIMEOUT_MS = 3000;

    function finish(isValid: boolean) {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve(isValid);
    }

    const timer = setTimeout(() => finish(false), TIMEOUT_MS);

    img.onload = () => finish(true);
    img.onerror = () => finish(false);

    img.src = url;
  });
};

export const getVariationsValidationRules = (
  index: number,
  variationsLength: number,
): VariationValidationRules => {
  return {
    [`variations.${index}.fairMarketValue`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: string | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the fair market value';
        }
        if (value && Number(value) < 0) {
          return 'Fair market value must be greater than or equal to 0';
        }
      },
    },
    [`variations.${index}.imgUrl`]: {
      validate: {
        validUrl: (value: string) =>
          isProductImageUrlValid(value) || 'Please enter a valid URL',
        validContentType: async (value: string) => {
          const isValid = await isProductImageContentTypeValid(value);
          return isValid || 'Invalid product image URL or unsupported format';
        },
      },
      required: {
        value: variationsLength > 1,
        message: 'Required',
      },
    },
    [`variations.${index}.logisticsRef`]: {
      required: {
        value: true,
        message: 'Required',
      },
    },
    [`variations.${index}.quantity`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: string | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the quantity';
        }
        if (value && Number(value) < 1) {
          return 'Quantity must be greater than 0';
        }
      },
    },
    [`variations.${index}.variantName`]: {
      required: {
        value: variationsLength > 1,
        message: 'Required',
      },
    },
  };
};

export const getProductDimensionsValidationRules = (
  number: number,
): DimensionValidationRules => {
  return {
    [`products.${number}.dimensions.format`]: {
      required: {
        value: true,
        message: 'Please select a dimension format',
      },
    },
    [`products.${number}.dimensions.depth`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the depth';
        }
        if (value && value < 0) {
          return 'Depth must be greater than or equal to 0';
        }
      },
    },
    [`products.${number}.dimensions.height`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the height';
        }
        if (value && value < 0) {
          return 'Height must be greater than or equal to 0';
        }
      },
    },
    [`products.${number}.dimensions.width`]: {
      required: {
        value: true,
        message: 'Required',
      },
      validate: (value: number | undefined) => {
        if (!validateNumber(value)) {
          return 'Please enter a valid number for the width';
        }
        if (value && value < 0) {
          return 'Width must be greater than or equal to 0';
        }
      },
    },
  };
};
